import { Injectable } from '@angular/core';
import { IPaginatorService } from './base';
import { HttpClient } from '@angular/common/http';
import { BasePaginator, Paginator } from '../model/base/base-model';
import { BluePrint, BlueprintFilter } from '../model/blue-print';
import {
    CatalogFilters,
    CatalogFiltersFeatures,
    AvailableCatalog
} from '../model/available-catalogs';
import { BluePrintList, BlueprintVersion, InventoryCatalog } from '../model/blueprint-list';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { tap, finalize, share } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class BlueprintService extends IPaginatorService {
    readonly baseEndpoint = 'Blueprint';
    private cache: any = <any>{};
    private cachedObservable: Observable<any> = <Observable<any>>{};

    constructor(private http: HttpClient) {
        super();
    }

    deleteBlueprint(Id: string): Observable<BluePrintList> {
        return this.http.delete<BluePrintList>(this.baseUrl + this.baseEndpoint + '/' + Id);
    }

    getBluePrints(): Observable<BluePrint[]> {
        return this.http.get<BluePrint[]>(this.baseUrl + this.baseEndpoint + '/GetBluePrints/');
    }

    editBlueprintById(id: string, bluePrint: any) {
        return this.http.put(this.baseUrl + this.baseEndpoint + '/' + id + '?id=' + id, bluePrint);
    }

    getPaginatedPostData(filter) {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/GetPaginatedPost', filter);
    }

    getPaginatedData(filter): Observable<any> {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/GetPaginated', filter);
    }

    getCatalogSummary(filter): Observable<any> {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/GetCatalogSummary', filter);
    }

    /** Converts plain-language Blueprint Catalog intent into editable structured filter text. */
    translateCatalogQuery(prompt: string): Observable<{ SuggestedQuery: string }> {
        return this.http.post<{ SuggestedQuery: string }>(
            this.baseUrl + this.baseEndpoint + '/TranslateCatalogQuery',
            { Query: prompt }
        );
    }

    getPaginatedDataScrumboard<BluePrintList>(
        sort: string,
        order: string,
        page: number,
        size: number,
        filter: string = '',
        state: string[] = [],
        cloud: string[] = []
    ): Observable<BasePaginator<BluePrintList>[]> {
        let url = this.baseUrl + this.baseEndpoint + '/GetPaginatedForBoard';
        let params: BlueprintFilter = {
            Filter: filter,
            PageNumber: page,
            PageSize: size,
            Order: order,
            Sort: sort,
            States: state,
            Clouds: cloud
        };

        return this.http.post<BasePaginator<BluePrintList>[]>(url, params);
    }

    getPaginatedDataScrumboardInventory<BluePrintList>(
        sort: string,
        order: string,
        page: number,
        size: number,
        state: string[] = []
    ): Observable<BasePaginator<BluePrintList>[]> {
        let url = this.baseUrl + 'Inventory/GetPaginatedForBoard';
        let params: BlueprintFilter = {
            Filter: '',
            PageNumber: page,
            PageSize: size,
            Order: order,
            Sort: sort,
            States: state
        };

        return this.http.post<BasePaginator<BluePrintList>[]>(url, params);
    }

    getTerraformCode(bluePrint): Observable<any> {
        return this.http.post<any>(environment.WORKER_URL + 'GenerateTerraformCode', bluePrint);
    }

    // POST GenerateCliByArtifact
    // Type: Blueprint, Inventory
    // Data: Object
    generateCliByArtifact(bluePrint): Observable<any> {
        return this.http.post<any>(environment.WORKER_URL + 'GenerateCliByArtifact', bluePrint);
    }

    generateDeleteCommandsByArtifact(bluePrint): Observable<any> {
        return this.http.post<any>(environment.WORKER_URL + 'GenerateDeleteCommandsByArtifact', bluePrint);
    }

    getBicepData(IacCode): Observable<any> {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/GenerateBicepByTerraform', IacCode);
    }

    getTerraformCodeById(id): Observable<any> {
        return this.http.get<BluePrintList>(environment.WORKER_URL +'GenerateTerraformCodeByBlueprintId/' + id);
    }
    
    getFormattedText(bluePrint): Observable<any> {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/GenerateFormattedText', bluePrint);
    }

    getPaginatedDataMarketplace<BlueprintMarketplace>(
        params
    ): Observable<BasePaginator<BlueprintMarketplace>> {
        let url = this.baseUrl + this.baseEndpoint + '/GetPaginated';
        return this.http.post<BasePaginator<BlueprintMarketplace>>(url, params);
    }

    purchaseBlueprintMarketplace(Id: string): Observable<BluePrintList> {
        return this.http.post<BluePrintList>(
            this.baseUrl + this.baseEndpoint + '/Purchase/' + Id,
            {}
        );
    }
    
    getAllVersionsByRecordGroupId(recordGroupId: string): Observable<any[]> {
        return this.http.get<any[]>(this.baseUrl + this.baseEndpoint + '/GetAllVersionsByRecordGroupId/' + recordGroupId);
    }

    getBluePrintById(Id: string): Observable<BluePrintList> {
        return this.http.get<BluePrintList>(this.baseUrl + this.baseEndpoint + '/getById/' + Id);
    }

    updateStatusBlueprint(Id: string, state: number): Observable<BluePrintList> {
        return this.http.get<BluePrintList>(
            this.baseUrl + this.baseEndpoint + '/ChangeState/' + Id + '/' + state
        );
    }

    deleteBlueprints(blueprints: any[]): Observable<any[]> {
        return this.http.post<any[]>(
            this.baseUrl + this.baseEndpoint + '/DeleteBlueprints',
            blueprints
        );
    }

    getMatchingBlueprintIds(filter: any): Observable<string[]> {
        return this.http.post<string[]>(
            this.baseUrl + this.baseEndpoint + '/GetCatalogIds',
            filter
        );
    }

    getBlueprintsForSelect(): Observable<any> {
        return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/GetBlueprintsForSelect');
    }

    postBluePrint(bluePrint: any) {
        return this.http.post(this.baseUrl + this.baseEndpoint + '', bluePrint);
    }

    getAvailableBlueprints<AvailableCatalog>(
        param: Paginator
    ): Observable<BasePaginator<AvailableCatalog>> {
        return this.http.post<BasePaginator<AvailableCatalog>>(
            this.baseUrl + `Inventory/GetPaginated`,
            param
        );
    }

    GetInventoryForUpdate(bpId: string): Observable<AvailableCatalog[]> {
        return this.http.get<AvailableCatalog[]>(
            this.baseUrl + 'Inventory/GetInventoryForUpdate/' + bpId
        );
    }

    updateStatusInventory(Id: string, state: number): Observable<BluePrintList> {
        return this.http.get<BluePrintList>(
            this.baseUrl + 'Inventory' + '/ChangeState/' + Id + '/' + state
        );
    }

    GetAttributesByCollectionEntry(collectionEntryId: string): Observable<any[]> {
        return this.http.get<any[]>(
            this.baseUrl + 'CollectionEntry/GetAttributesByCollectionEntry/' + collectionEntryId
        );
    }

    getDependencyMapRegions(dependencyMapId: string): Observable<string[]> {
        return this.http.get<string[]>(this.baseUrl + 'RegionInfo/GetForCloud/' + dependencyMapId);
    }

    getVpc(accountId: string, region: string): Observable<string[]> {
        return this.http.get<string[]>(
            this.baseUrl + 'CloudData/GetVpcs/' + accountId + '/' + region
        );
    }

    getInventoryForSelect(dependencyMapId: string, region: string): Observable<string[]> {
        return this.http.get<string[]>(
            this.baseUrl + 'Inventory/GetInventoryForSelect/' + dependencyMapId + '/' + region
        );
    }

    getSubnet(accountId: string, region: string, vpcId: string): Observable<any[]> {
        let request = {
            AccountId: accountId,
            Region: region,
            VpcId: vpcId
        };
        return this.http.post<any[]>(this.baseUrl + 'CloudData/GetSubnets', request);
    }

    getNIC(request): Observable<any[]> {
        return this.http.post<any[]>(this.baseUrl + 'CloudData/GetNics', request);
    }

    sendCompliance(request): Observable<any[]> {
        return this.http.post<any[]>(this.baseUrl + 'App/PostAssociation', request);
    }

    getInventoryById(dependencyMapId: string): Observable<InventoryCatalog> {
        return this.http.get<InventoryCatalog>(
            this.baseUrl + 'Inventory/GetById/' + dependencyMapId
        );
    }

    uploadBlueprintImage(obj: any): Observable<any> {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/UploadBlueprintImage', obj);
    }

    getHistory(recordGroupId: string, isRefresh: boolean = true): Observable<BlueprintVersion[]> {
        let observable: Observable<any>;
        if (this.cache[recordGroupId] && !isRefresh) {
        observable = of(this.cache[recordGroupId]);
        }  else if (this.cachedObservable[recordGroupId] && !isRefresh) {
        observable = this.cachedObservable[recordGroupId];
        } else {
        this.cachedObservable[recordGroupId] = this.http.get<any[]>(this.baseUrl + this.baseEndpoint + '/GetAllVersions/' + recordGroupId)
            .pipe(
            tap(res => this.cache[recordGroupId] = res),
            share(),
            finalize(() => this.cachedObservable[recordGroupId] = null)
            );
        observable = this.cachedObservable[recordGroupId];
        }
        return observable;
    }

    getAllVersionsForSelect(blueprintId: string): Observable<BlueprintVersion[]> {
        return this.http.get<BlueprintVersion[]>(
            this.baseUrl + this.baseEndpoint + '/GetAllVersionsForSelect/' + blueprintId
        );
    }
    reviewBluePrint(review) {
        return this.http.post(this.baseUrl + this.baseEndpoint + '/ReviewBlueprint', review);
    }

    getBluePrintHistory(source: string, target: string) {
        if (!source) {
            return this.http.get<any>(this.baseUrl + this.baseEndpoint + '/GetComparision/' + target);
        }

        return this.http.get<any>(
            this.baseUrl + this.baseEndpoint + '/GetComparision/' + target + '/' + source
        );
    }

    parseTemplates(blueprintId: string) {
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/ParseTemplates/' + blueprintId, {});
    }

    rollback(recordGroupId, version) {
        return this.http.get(
            this.baseUrl + this.baseEndpoint + '/Rollback/' + recordGroupId + '/' + version
        );
    }

    getBluePrintforComparison(source): Observable<[]> {
        return this.http.post<[]>(
            this.baseUrl + this.baseEndpoint + '/PostBlueprintForComparision',
            source
        );
    }

    DeleteWithInstance(source): Observable<[]> {
        return this.http.post<[]>(this.baseUrl + this.baseEndpoint + '/DeleteWithInstance', source);
    }

    checkNameAvailibility(name: string): Observable<string[]> {
        return this.http.get<string[]>(this.baseUrl + this.baseEndpoint + '/CheckName/' + encodeURIComponent(name));
    }

    getForSelectPaginated(params){
        let url = this.baseUrl + this.baseEndpoint + '/GetForSelectPaginated'
        return this.http.post<any>(url, params);
    }

    GenerateBlueprintByTerraform(terraform): Observable<any> {
        return this.http.post<any>(environment.WORKER_URL + 'GenerateBlueprintByTerraform', terraform);
    }
}
